import $ from "../../libs/jquery-3.3.1.min.js";
import {CustomEase, CustomWiggle, gsap} from "../../libs/gsap/all";
import * as PIXI from "pixi.js";
import 'pixi.js/math-extras'
import {BaseMiniGame} from "./BaseMiniGame";
import * as Impetus from "../../libs/impetus-pixijs.js";

// Game description : Sliding in image to find an object
export class GameA extends BaseMiniGame {

  // Constructor
  constructor(data) {
    super(data);
    this.appWidth = this.data.appDimensions.width;
    this.appHeight = this.data.appDimensions.height;
    this.dragBoxWidth = this.data.dragBox.width;
    this.dragBoxHeight = this.data.dragBox.height;
    this.bounce = this.data.dragBox.bounce;
    this.appPadding = 85; // empty space that surround the game

    this.objectiveBox = this.data.objectiveBox;

    this.initialBackgroundPos = {x: 0, y: 0};

    this.backgroundScale = this.data.dragBox.backgroundScale;
    this.impetus = null;

    this.$screenEl = null;
    this.beginningTimeline = null;

    this.dragTimeout = false;
    this.draggedOnce = false;

    this.lastValidateTimestamp = 0;

    // Register GSAP plugins
    gsap.registerPlugin(CustomEase, CustomWiggle);
  }

  async assetLoader() {
    await super.assetLoader();

    if (this.data.medias && this.data.medias.length) {
      for (let i = 0; i < this.data.medias.length; i++) {
        this.assets[this.data.medias[i].name] = await PIXI.Assets.load(this.data.medias[i].url);
      }
    }
  }

  async buildStage() {
    // this.stage.eventMode = 'static';

    this.$screenEl = $(this.canvas).parents(".screen");

    this.questionTextContainer = this.$screenEl.find(".game-question");
    this.questionTextContainer.css({
      top: this.data.question.y,
      left: this.data.question.x,
    });

    this.dragBoxContainer = new PIXI.Container();
    this.dragBoxContainer.width = this.dragBoxWidth;
    this.dragBoxContainer.height = this.dragBoxHeight;
    let baseY = this.appHeight / 2 - this.dragBoxHeight / 2;
    this.dragBoxContainer.position.set(this.appPadding, baseY);
    this.dragBoxContainer.mask = new PIXI.Graphics().rect(this.appPadding, baseY, this.dragBoxWidth, this.dragBoxHeight).fill({color: 0xffffff});

    this.movingContainer = new PIXI.Container();
    this.movingContainer.cursor = "grab";

    // Prepare background
    this.backgroundSpriteEN = new PIXI.Sprite(this.assets.background_en);
    this.backgroundSpriteFR = new PIXI.Sprite(this.assets.background_fr);
    this.backgroundSpriteEN.width = this.backgroundSpriteFR.width = this.backgroundSpriteEN.width * this.backgroundScale;
    this.backgroundSpriteEN.height = this.backgroundSpriteFR.height = this.backgroundSpriteEN.height * this.backgroundScale;

    // Initial position
    this.initialBackgroundPos = {
      x: -this.backgroundSpriteEN.width + this.dragBoxWidth,
      y: -this.backgroundSpriteEN.height / 2 + this.dragBoxHeight / 2
    };

    // Set the moving container the same dimension as the background image
    this.movingContainer.width = this.backgroundSpriteEN.width;
    this.movingContainer.height = this.backgroundSpriteEN.height;

    this.stage.addChild(this.dragBoxContainer);
    this.movingContainer.addChild(this.backgroundSpriteEN, this.backgroundSpriteFR);
    this.dragBoxContainer.addChild(this.movingContainer);

    // Pointer icon CTA
    this.pointerSprite = new PIXI.Sprite(this.assets["pointer"]);
    this.pointerSprite.setSize(195, 157);
    this.pointerSprite.position.set(855, 630);
    this.pointerSprite.originalPosition = this.pointerSprite.position;
    this.stage.addChild(this.pointerSprite);

    // conclusion image
    if (this.data.conclusion.image) {
      this.conclusionImageContainer = new PIXI.Container();

      this.conclusionImageSpriteEN = new PIXI.Sprite(this.assets[this.data.conclusion.image.en]);
      this.conclusionImageSpriteFR = new PIXI.Sprite(this.assets[this.data.conclusion.image.fr]);
      this.conclusionImageSpriteEN.width = this.conclusionImageSpriteFR.width = this.dragBoxWidth;
      this.conclusionImageSpriteEN.height = this.conclusionImageSpriteFR.height = this.dragBoxHeight;
      this.conclusionImageContainer.addChild(this.conclusionImageSpriteEN, this.conclusionImageSpriteFR);

      this.conclusionImageContainer.position.x = this.appPadding;
      this.conclusionImageContainer.position.y = this.appPadding;
      this.stage.addChild(this.conclusionImageContainer);
    }

    // Setup impetus
    this.impetus = new Impetus({
      source: this.movingContainer,
      bounce: this.bounce,
      boundX: [(-this.movingContainer.width + this.dragBoxWidth), 0],
      // boundY: [(-this.movingContainer.height + this.dragBoxHeight), 0],
      multiplier: 0.65,
      update: (x, y) => {
        // Move the container around
        this.movingContainer.position.x = x;

        // Show confirm button when objective visible
        if (this.dragTimeout) {
          clearTimeout(this.dragTimeout);
        }
        this.dragTimeout = setTimeout(() => {
          this.validateAnswers();
        }, Math.max(0, this.lastValidateTimestamp - Date.now() + 50));

        // Hide call to action pointer
        if (!this.draggedOnce) {
          this.draggedOnce = true;
          gsap.to(this.pointerSprite, {
            alpha: 0.0,
            ease: "power1.inOut",
            duration: 0.50
          });
        }
      }
    });

    // Position the image at its center so we can drag in all directions
    // center it so we can drag left and right
    // We need to set the values using the {... } method because if we set it like
    // this.movingContainer.position = this.initialBackgroundPos;
    // It references the initial pos to the moving container pos instead of actually setting it
    // So the initial pos is updated at the same time as the moving container and we do not want it to change
    this.movingContainer.position = {...this.initialBackgroundPos};

    // We need to set the same initial value for impetus so it does not think it's still at 0,0
    this.impetus.setValues(this.initialBackgroundPos.x, this.initialBackgroundPos.y);

    // This is the "box" that needs to be visible in the viewport to complete the challenge
    this.objectiveBoxSprite = new PIXI.Sprite(PIXI.Texture.WHITE);
    this.objectiveBoxSprite.width = this.objectiveBox.width;
    this.objectiveBoxSprite.height = this.objectiveBox.height;
    this.objectiveBoxSprite.position.x = this.objectiveBox.x;
    this.objectiveBoxSprite.position.y = this.objectiveBox.y;
    this.objectiveBoxSprite.alpha = 0.0; // debug objective box
    this.movingContainer.addChild(this.objectiveBoxSprite);

    // Used for debugging in pixijs devtool
    //globalThis.__PIXI_APP__ = this;
  }

  validateAnswers() {
    this.lastValidateTimestamp = Date.now();
    const x = this.movingContainer.position.x;
    const y = this.movingContainer.position.y;

    let viewingArea = {
      x1: -x,
      x2: -x + this.dragBoxWidth,
      y1: -y,
      y2: -y + this.dragBoxHeight,
    }

    if (
      (this.objectiveBoxSprite.x >= viewingArea.x1) &&
      ((this.objectiveBoxSprite.x + this.objectiveBoxSprite.width) <= viewingArea.x2) &&
      (this.objectiveBoxSprite.y >= viewingArea.y1) &&
      ((this.objectiveBoxSprite.y + this.objectiveBoxSprite.height) <= viewingArea.y2)
    ) {
      // Objective is on screen! Complete the challenge
      this.$screenEl.find(".game-confirm-button").addClass("shown");
    }
  }

  buildBegginingTimeline() {
    this.beginningTimeline = gsap.timeline({paused: true});

    // Question text
    this.beginningTimeline.set(this.questionTextContainer, {
      alpha: 1.0
    });
    this.beginningTimeline.fromTo(this.questionTextContainer, {
      alpha: 0,
    }, {
      alpha: 1,
      ease: "power1.inOut",
      duration: 0.50,
      immediateRender: true
    });

    // Pointer
    this.beginningTimeline.fromTo(this.pointerSprite, {
      alpha: 0.0,
    }, {
      alpha: 1,
      ease: "power1.inOut",
      duration: 0.50,
      immediateRender: true
    }, "+=0.5");
    this.beginningTimeline.to(this.pointerSprite, {
      pixi: {
        x: this.pointerSprite.position.x - 25,
        y: this.pointerSprite.position.y - 25,
      },
      ease: "power1.inOut",
      duration: 0.75,
      repeat: -1,
      yoyo: true
    });
    this.beginningTimeline.add(() => {
      this.movingContainer.eventMode = "static";
    }, "<=");
  }

  buildConclusionTimeline() {

    this.conclusionTimeline = gsap.timeline({paused: true});

    this.conclusionTimeline.addLabel("fade-out-question");

    this.conclusionTimeline.to(this.questionTextContainer, {
      alpha: 0.0,
      duration: 0.25,
      delay: 0.70,
      ease: "power1.inOut"
    }, "fade-out-question");

    this.conclusionTimeline.to(this.choicesObjects, {
      pixi: {
        alpha: 0,
      },
      duration: 0.25,
      delay: 0.70,
      ease: "power1.inOut",
    }, "fade-out-question");

    // force game conclusion to be active
    this.conclusionTimeline.set(this.$screenEl.find(".game-conclusion"), {
      alpha: 1,
    });

    if (this.$screenEl.find(".container-title").length) {
      this.conclusionTimeline.fromTo(this.$screenEl.find(".container-title"), {
        '--title-width': "0%"
      }, {
        '--title-width': "100%",
        duration: 0.45,
        ease: "power2.out",
        immediateRender: true
      });

      this.conclusionTimeline.fromTo(this.$screenEl.find(".container-title-inner"), {
        alpha: 0.0
      }, {
        alpha: 1.0,
        duration: 0.30,
        ease: "power1.in",
        immediateRender: true
      });
    }

    this.conclusionTimeline.fromTo(this.$screenEl.find('.container-content'), {
      alpha: 0,
    }, {
      alpha: 1,
      ease: "power1.inOut",
      duration: 0.30,
      immediateRender: true
    });

    if (this.conclusionImageContainer) {
      console.log("should show")
      this.conclusionTimeline.fromTo(this.conclusionImageContainer, {
          pixi: {
            alpha: 0,
          }
        },
        {
          pixi: {
            alpha: 1,
          },
          duration: 0.25,
          ease: "power1.inOut",
        }, "<=");
    }

    if (this.$screenEl.find(".container-note").length) {
      this.conclusionTimeline.fromTo(this.$screenEl.find(".container-note"), {
        y: "+=100%"
      }, {
        y: 0,
        duration: 1.50,
        ease: "power1.inOut",
        immediateRender: true
      }, "+=0.75");

      this.conclusionTimeline.fromTo(this.$screenEl.find(".container-note"), {
        autoAlpha: 0
      }, {
        autoAlpha: 1,
        duration: 0.1,
        ease: "linear",
        immediateRender: true
      }, '<=');
    }

    // Show container button
    if (this.$screenEl.find(".container-button").length) {
      this.conclusionTimeline.fromTo(this.$screenEl.find(".container-button"), {
        alpha: 0.0,
        scale: 0.0
      }, {
        alpha: 1.0,
        scale: 1.0,
        duration: 0.75,
        ease: "power2.out",
        immediateRender: true,
        onStart: () => {
          // Disable button animations
          this.$screenEl.find("button.pulse-animation, button.up-down-animation").removeClass("play-animation");
        },
        onComplete: () => {
          this.$screenEl.find(".game-conclusion").removeClass("disabled");

          // Enable button animations
          this.$screenEl.find("button.pulse-animation, button.up-down-animation").addClass("play-animation");
        }
      });

      // Disable click on this section
      this.$screenEl.find(".game-conclusion").addClass("disabled");
    }

  }

  goToConclusion() {
    this.$screenEl.find(".game-confirm-button").addClass("disabled");
    this.conclusionTimeline.play(0);
  }

  beforeAnimateIn() {
    // rebuild timeline

    if (this.beginningTimeline) {
      this.beginningTimeline.pause();
      // this.conclusionTimeline.progress(0, true);
    } else {
      this.buildBegginingTimeline();
    }

    if (this.conclusionTimeline) {
      this.conclusionTimeline.pause();
      // this.conclusionTimeline.progress(0, true);
    } else {
      this.buildConclusionTimeline();
    }
  }

  begin() {
    this.beginningTimeline.play(0);
  }

  reset() {
    //console.log("gameA Reset");

    this.draggedOnce = false;
    if (this.dragTimeout) {
      clearTimeout(this.dragTimeout);
      this.dragTimeout = false;
    }

    this.movingContainer.eventMode = "none";
    this.pointerSprite.position = this.pointerSprite.originalPosition;
    this.$screenEl.find(".game-confirm-button").removeClass("shown disabled");

    this.movingContainer.position = {...this.initialBackgroundPos};
    this.impetus.setValues(this.initialBackgroundPos.x, this.initialBackgroundPos.y);

    // rebuild timeline
    if (this.beginningTimeline) {
      this.beginningTimeline.kill();
      this.beginningTimeline = null;
    }
    this.buildBegginingTimeline();

    if (this.conclusionTimeline) {
      this.conclusionTimeline.kill();
      this.conclusionTimeline = null;
    }
    this.buildConclusionTimeline();
  }

  interfaceLanguageSwitch(lang) {
    super.interfaceLanguageSwitch(lang);

    const imageShown = lang === "fr" ? this.backgroundSpriteFR : this.backgroundSpriteEN;
    const imageHidden = lang === "fr" ? this.backgroundSpriteEN : this.backgroundSpriteFR;
    const conclusionImageShown = lang === "fr" ? this.conclusionImageSpriteFR : this.conclusionImageSpriteEN;
    const conclusionImageHidden = lang === "fr" ? this.conclusionImageSpriteEN : this.conclusionImageSpriteFR;

    imageShown.alpha = 1;
    imageHidden.alpha = 0;
    conclusionImageShown.alpha = 1;
    conclusionImageHidden.alpha = 0;
  }
}
